import {
  X,
  Image,
  FileText,
  Music,
  Video,
  File,
  Loader2,
  Check,
  AlertCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import type { ChatMediaFile } from "@/app/api/types/chat-sessions";

export type UploadStatus = "pending" | "uploading" | "uploaded" | "error";

export interface PendingFile {
  id: string;
  file: File;
  preview?: string;
  type: ChatMediaFile["type"];
  uploadStatus: UploadStatus;
  uploadedUrl?: string;
  mediaId?: string;
  error?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

interface MediaPreviewCardProps {
  readonly file: PendingFile;
  readonly onRemove: () => void;
}

export function MediaPreviewCard({ file, onRemove }: MediaPreviewCardProps) {
  const { t } = useTranslation();
  const FileIcon = {
    image: Image,
    pdf: FileText,
    audio: Music,
    video: Video,
    document: FileText,
    file: File,
  }[file.type];

  const isUploading = file.uploadStatus === "uploading";
  const isUploaded = file.uploadStatus === "uploaded";
  const isError = file.uploadStatus === "error";

  return (
    <div className="relative group bg-muted rounded-lg overflow-hidden w-20 h-20 flex-shrink-0">
      {file.preview ? (
        <img
          src={file.preview}
          alt={file.file.name}
          className={`w-full h-full object-cover transition-opacity ${isUploading ? "opacity-50" : ""}`}
        />
      ) : (
        <div
          className={`w-full h-full flex flex-col items-center justify-center p-2 transition-opacity ${isUploading ? "opacity-50" : ""}`}
        >
          <FileIcon className="h-6 w-6 text-muted-foreground mb-1" />
          <span className="text-[10px] text-muted-foreground text-center truncate w-full">
            {file.file.name}
          </span>
        </div>
      )}

      {/* Upload overlay */}
      {isUploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
          <Loader2 className="h-5 w-5 animate-spin text-white" />
        </div>
      )}

      {/* Upload status badge */}
      {isUploaded && (
        <div className="absolute top-1 left-1 p-0.5 rounded-full bg-green-500 text-white">
          <Check className="h-2.5 w-2.5" />
        </div>
      )}
      {isError && (
        <div
          className="absolute top-1 left-1 p-0.5 rounded-full bg-destructive text-destructive-foreground"
          title={file.error ?? t("common.sidePanelChat.uploadFailed")}
        >
          <AlertCircle className="h-2.5 w-2.5" />
        </div>
      )}

      {/* Upload progress bar */}
      {isUploading && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
          <div className="h-full bg-primary animate-pulse w-full" />
        </div>
      )}

      <button
        onClick={onRemove}
        className="absolute top-1 right-1 p-1 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
        title={t("common.remove")}
      >
        <X className="h-3 w-3" />
      </button>
      {!isUploading && (
        <div className="absolute bottom-1 left-1 px-1 py-0.5 rounded text-[9px] bg-black/50 text-white">
          {formatFileSize(file.file.size)}
        </div>
      )}
    </div>
  );
}
